import { message as wkMessage, toast as wkToast } from '@wk/wk-uni-lib';
import axios, {
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosHeaders,
  AxiosError,
} from 'axios';
import { redirect } from 'next/navigation';
import qs from 'query-string';

import { REFRESH_TOKEN, TOKEN } from '@/constants';
import CacheStorage from '@/utils/cache-storage';
import { isAppEnv, isServerSide } from '@/utils/env';
import { removeTokenInfo, setTokenInfo } from '@/utils/helpers';
import { getLanguage } from '@/utils/language';
import { reportServerLog } from '@/utils/server-log';
import { getServerToken } from '@/utils/server-token';
import { getAPIBaseUrl, getBrowserUrl, parseUrl } from '@/utils/url';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipErrorHandler?: boolean | number[];
    skipTransformResponse?: boolean;
    skipErrorAccess?: boolean;
  }
}

// Token 获取已移至 @/utils/server-token.ts，统一使用 getServerToken()

/**
 * 获取 sign 参数
 */
const getSign = async (): Promise<string | undefined> => {
  if (isServerSide()) {
    try {
      const { headers } = await import('next/headers');
      const headersList = await headers();
      const url = headersList.get('referer') || '';
      if (url) {
        const urlObj = new URL(url);
        return urlObj.searchParams.get('sign') || undefined;
      }
    } catch {
      return undefined;
    }
  } else {
    const { sign } = qs.parse(globalThis.location?.search || '');
    return sign as string | undefined;
  }
  return undefined;
};

/**
 * 统一处理错误信息
 */
const handleErrMsg = (message: string, err: AxiosError) => {
  if (isServerSide()) {
    throw new Error(message);
  } else {
    if (isAppEnv()) {
      wkToast({
        description: message,
        duration: 3000,
      });
    } else {
      wkMessage.error(message);
    }
    throw err;
  }
};

const isNetworkError = (error: AxiosError) => {
  return ['ERR_NETWORK', 'ECONNABORTED', 'ENOTFOUND'].includes(
    error?.code || '',
  );
};

/**
 * 创建统一的 axios 实例
 */
const createUnifiedAxios = () => {
  const instance = axios.create({
    timeout: 30000,
    baseURL: getAPIBaseUrl(process.env.NEXT_PUBLIC_API_DOMAIN),
  });

  const requestHandler = async (requestConfig: InternalAxiosRequestConfig) => {
    // 确保 headers 存在
    if (!requestConfig.headers) {
      requestConfig.headers = new AxiosHeaders();
    }

    if (isServerSide()) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (requestConfig as any).metadata = { startTime: new Date().getTime() };
      // 服务端：异步获取
      const token = await getServerToken();
      requestConfig.headers.Authorization = token ? `Bearer ${token}` : '';

      const language = await getLanguage();
      requestConfig.headers['Accept-Language'] =
        language === 'zh' ? 'zh-CN' : 'en';

      const sign = await getSign();
      if (sign) {
        requestConfig.headers.sign = encodeURIComponent(sign);
      }
    } else {
      // 客户端：同步获取（恢复原来的逻辑）
      const { sign } = qs.parse(globalThis.location?.search || '');
      const token = CacheStorage.getItem(TOKEN);

      requestConfig.headers.Authorization = token ? `Bearer ${token}` : '';
      requestConfig.headers['Accept-Language'] =
        globalThis?.location?.href?.includes?.('/zh/') ? 'zh-CN' : 'en';

      if (sign) {
        requestConfig.headers.sign = encodeURIComponent(sign as string);
      }
    }

    // 设置平台标识
    requestConfig.headers.platform = 'web';

    return requestConfig;
  };

  const responseHandler = async (res: AxiosResponse) => {
    const { config, status } = res || {};
    const { baseURL, url, method, data, headers, skipTransformResponse } =
      config || {};

    /* 请求日志，在vercel可以查看 */
    if (isServerSide()) {
      const endTime = new Date().getTime();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const startTime = (res?.config as any)?.metadata?.startTime || endTime;
      const logItem = {
        result: 'success',
        url: `${baseURL}${url?.startsWith('/') ? '' : '/'}${url}`,
        method,
        ...(method === 'get' ? {} : { requestData: JSON.stringify(data) }),
        requestHeaders: JSON.stringify({ ...headers, Authorization: '***' }),
        responseStatus: status,
        duration: `${endTime - startTime}ms`,
        responseHeaders: JSON.stringify(res.headers),
        responseData: JSON.stringify(res.data),
      };
      // eslint-disable-next-line no-console
      console.log(logItem);
      reportServerLog(logItem);
    }

    if (skipTransformResponse) {
      return res;
    }

    return res.data;
  };

  const handleTokenRefreshInServer = async (
    page: '401' | '406',
    status?: number,
  ) => {
    const [lang, returnUrl] = await Promise.all([
      getLanguage(),
      getBrowserUrl(),
    ]);
    if (page === '406' && status) {
      const { path, originQuery } = parseUrl(returnUrl);
      const pathname = path;
      const search = originQuery.toString();
      const encodeReturnUrl = encodeURIComponent(
        `/${lang}${pathname}${search ? `?${search}` : ''}`,
      );
      redirect(`/${lang}/406?status=${status}&return-url=${encodeReturnUrl}`);
    } else {
      redirect(
        `/${lang}/401${returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : ''}`,
      );
    }
  };

  const errorHandler = async (err: AxiosError) => {
    const { baseURL, url, method, headers, skipErrorHandler } =
      err.config || {};

    if (skipErrorHandler) {
      throw err;
    }

    /* 错误日志，在vercel可以查看 */
    if (isServerSide()) {
      const endTime = new Date().getTime();
      const startTime = (err?.config as any)?.metadata?.startTime || endTime;
      // eslint-disable-next-line no-console
      const log: Record<string, any> = {
        result: 'error',
        url: `${baseURL}${url?.startsWith('/') ? '' : '/'}${url}`,
        method,
        requestHeaders: JSON.stringify({ ...headers, Authorization: '***' }),
        duration: `${endTime - startTime}ms`,
        errorCode: err.code,
      };
      if (method !== 'get') {
        log.requestData = JSON.stringify(err?.config?.data || {});
      }
      if (err.status) {
        log.errStatus = err.status;
      }
      if (err.message) {
        log.errMessage = err.message;
      }
      if (err.response) {
        log.stuErrData = JSON.stringify(err?.response?.data || {});
      }
      console.error(log);
      reportServerLog(log);
    }

    const lang = await getLanguage();
    const isEn = lang === 'en';

    // 网络错误
    if (isNetworkError(err)) {
      const errorMessage = isEn
        ? 'Network error. Please try again later.'
        : '网络异常，请稍后再试';
      handleErrMsg(errorMessage, err);
    }

    if (!err.response) {
      throw err;
    }

    const { status, config, data } = err.response;
    const { errors, message } =
      (data as {
        errors: Record<string, string>;
        message: string;
      }) || {};
    const errMsg = (errors && errors[Object.keys(errors)[0]]) || message;

    const shouldSkipError = (status: number) => {
      if (Array.isArray(config?.skipErrorHandler)) {
        return config.skipErrorHandler.includes(status);
      }
      return !!config?.skipErrorHandler;
    };

    // 维护中状态 (433)
    if (+status === 433) {
      redirect(`/${lang}/notice/now`);
    }

    // 未授权状态 (401)
    if (+status === 401 && !config?.skipErrorAccess && !shouldSkipError(401)) {
      const url = await getBrowserUrl();
      if (url.includes('token=')) {
        return;
      } else if (isServerSide()) {
        await handleTokenRefreshInServer('401');
      } else {
        removeTokenInfo(null, false);
      }
      return new Promise(() => {});
    }

    // Token 刷新相关错误 (406, 403)
    if ((+status === 406 || +status === 403) && !shouldSkipError(+status)) {
      if (isServerSide()) {
        await handleTokenRefreshInServer('406', status);
      } else {
        // 客户端：恢复原来的token刷新逻辑
        const url =
          +status === 406
            ? '/api/authorized/refresh_token'
            : '/api/authorized/refresh_token_V2';
        const requestData =
          +status === 406
            ? undefined
            : {
                refreshToken: CacheStorage.getItem(REFRESH_TOKEN),
              };
        instance
          .post<
            { content: { token: string; refreshToken: string } },
            { content: { token: string; refreshToken: string } }
          >(url, requestData)
          .then(res => {
            const { token, refreshToken = '' } = res?.content || {};
            setTokenInfo(token, refreshToken);
            window.location.reload();
          })
          .catch(() => {
            removeTokenInfo(null, true);
          });
      }
      return new Promise(() => {});
    }

    // 验证错误 (422)
    if (+status === 422 && !shouldSkipError(422)) {
      const clearError =
        errMsg || (isEn ? 'Unknown validation error' : '未知错误');
      handleErrMsg(clearError, err);
    }

    // 跳过错误处理
    if (shouldSkipError(+status)) {
      throw err;
    }

    // 其他错误
    if (typeof data === 'string' || errMsg) {
      handleErrMsg(errMsg || (isEn ? 'Unknown error' : '未知错误'), err);
    }

    throw err;
  };

  // 设置拦截器
  instance.interceptors.request.use(requestHandler);
  instance.interceptors.response.use(responseHandler, errorHandler);

  return instance;
};

// 创建单例实例
const apiClient = createUnifiedAxios();

export default apiClient;

// 导出便捷方法，保持向后兼容
export const { get, post, put, delete: del, patch, request } = apiClient;
